import React, { useState, useEffect } from 'react';
import Navbar from '../../components/LayoutComponents/Navbar/Navbar3';
import {
    Box, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, CircularProgress, Typography, TablePagination,
    TableFooter, Switch, Dialog, DialogActions, DialogContent,
    DialogContentText, DialogTitle, Button, Chip
} from '@mui/material';
import Sidebar2 from '../../components/LayoutComponents/Sidebar/Sidebar2';
import { useGetUsersForOwnerQuery, useUpdateUsersStatusMutation } from '../../Api/apiSlice';

const OwnerDashboard = () => {
    const search = "";
    const status = "";
    const role = "owner";

    const { data, error, isLoading } = useGetUsersForOwnerQuery({ search, status, role });
    const [updateUserStatus, { isLoading: isUpdating, error: updateError }] = useUpdateUsersStatusMutation();

    const [users, setUsers] = useState([]);
    const [openModal, setOpenModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);

    useEffect(() => {
        if (data) {
            setUsers(data.map(user => ({ ...user, isActive: !user.isDisable })));
        }
    }, [data]);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    const handleToggle = (user) => {
        setSelectedUser(user);
        setOpenModal(true);
    };

    const handleConfirmToggle = async () => {
        if (!selectedUser) return;

        try {
            const updatedUser = await updateUserStatus({ userId: selectedUser.id }).unwrap();
            setUsers(users.map(user =>
                user.id === selectedUser.id ? { ...user, isActive: !user.isActive } : user
            ));
        } catch (err) {
            console.error("Failed to update user status:", err);
        } finally {
            setOpenModal(false);
        }
    };

    return (
        <Box display="flex" flexDirection="row" gap={3} backgroundColor="#F4F7F7" sx={{overflow: "auto",}}>
            <Sidebar2 />
            <Box flex={10.2} p={3} sx={{ mt: '5.4rem' }}>
                <Navbar />
                <Paper elevation={3} sx={{ mt: 3, p: 2, borderRadius: 2 }}>
                    <Typography variant="h6" gutterBottom>Users List</Typography>

                    {isLoading ? (
                        <Box display="flex" justifyContent="center" alignItems="center" height={200}>
                            <CircularProgress />
                        </Box>
                    ) : error ? (
                        <Typography color="error">Failed to load data</Typography>
                    ) : (
                        // Add a Box wrapper to enable horizontal scrolling
                        <Box>
                            <TableContainer component={Paper} sx={{ maxHeight: '60vh', minWidth: '800px',overflowX: "auto", }}>
                                <Table stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell><b>Organization</b></TableCell>
                                            <TableCell><b>Name</b></TableCell>
                                            <TableCell><b>Email</b></TableCell>
                                            <TableCell><b>Phone</b></TableCell>
                                            <TableCell><b>Status</b></TableCell>
                                            <TableCell><b>Role</b></TableCell>
                                            <TableCell><b>Enabled</b></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {users.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((user) => (
                                            <TableRow key={user.id} hover>
                                                <TableCell>{user.Organization?.name || "N/A"}</TableCell>
                                                <TableCell>{user.name}</TableCell>
                                                <TableCell>{user.email}</TableCell>
                                                <TableCell>{user.phone}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={user.isActive ? "Active" : "Disabled"}
                                                        color={user.isActive ? "success" : "error"}
                                                        sx={{ fontWeight: 'bold' }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {user.role === "driver" ? "Driver" :
                                                        user.role === "superAdmin" ? "Super Admin" :
                                                            user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                                                </TableCell>
                                                <TableCell>
                                                    <Switch
                                                        checked={user.isActive}
                                                        onChange={() => handleToggle(user)}
                                                        color="primary"
                                                        disabled={isUpdating}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>

                                    <TableFooter>
                                        <TableRow>
                                            <TablePagination
                                                rowsPerPageOptions={[5, 10, 25]}
                                                count={users.length}
                                                rowsPerPage={rowsPerPage}
                                                page={page}
                                                onPageChange={(event, newPage) => setPage(newPage)}
                                                onRowsPerPageChange={(event) => {
                                                    setRowsPerPage(parseInt(event.target.value, 10));
                                                    setPage(0);
                                                }}
                                            />
                                        </TableRow>
                                    </TableFooter>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}

                    {updateError && (
                        <Typography color="error" sx={{ mt: 2 }}>
                            Failed to update user status. Please try again.
                        </Typography>
                    )}
                </Paper>
                <Dialog open={openModal} onClose={() => setOpenModal(false)}>
                    <DialogTitle>
                        {selectedUser?.isActive ? "Disable User" : "Enable User"}
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to {selectedUser?.isActive ? "disable" : "enable"} this user?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenModal(false)} color="secondary">Cancel</Button>
                        <Button onClick={handleConfirmToggle} color="primary" variant="contained" disabled={isUpdating}>
                            {isUpdating ? <CircularProgress size={24} /> : selectedUser?.isActive ? "Disable" : "Enable"}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Box>
    );
};

export default OwnerDashboard;
